import React, { createContext, useContext, useState, useCallback } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [bump, setBump] = useState(false);
  // items that should be checked out right now (either full cart or a single "Buy Now" item)
  const [checkoutItems, setCheckoutItems] = useState([]);

  const triggerBump = useCallback(() => {
    setBump(true);
    setTimeout(() => setBump(false), 400);
  }, []);

  const addToCart = useCallback(
    (product, qty = 1) => {
      setItems((prev) => {
        const existing = prev.find((it) => it.name === product.name);
        if (existing) {
          return prev.map((it) =>
            it.name === product.name ? { ...it, qty: it.qty + qty } : it
          );
        }
        return [...prev, { ...product, qty }];
      });
      triggerBump();
    },
    [triggerBump]
  );

  const removeFromCart = useCallback((name) => {
    setItems((prev) => prev.filter((it) => it.name !== name));
  }, []);

  const updateQty = useCallback((name, qty) => {
    setItems((prev) =>
      prev
        .map((it) => (it.name === name ? { ...it, qty: Math.max(1, qty) } : it))
        .filter((it) => it.qty > 0)
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const startCheckoutWithCart = useCallback(() => {
    setCheckoutItems(items);
  }, [items]);

  const startCheckoutWithSingle = useCallback((product, qty = 1) => {
    setCheckoutItems([{ ...product, qty }]);
  }, []);

  const removeCheckoutFromCart = useCallback(() => {
    setItems((prev) => prev.filter((it) => !checkoutItems.some((ci) => ci.name === it.name)));
    setCheckoutItems([]);
  }, [checkoutItems]);

  const cartCount = items.reduce((sum, it) => sum + it.qty, 0);
  const cartTotal = items.reduce(
    (sum, it) => sum + parseFloat(String(it.price).replace(/,/g, "")) * it.qty,
    0
  );

  const value = {
    items,
    addToCart,
    removeFromCart,
    updateQty,
    clearCart,
    cartCount,
    cartTotal,
    bump,
    checkoutItems,
    startCheckoutWithCart,
    startCheckoutWithSingle,
    removeCheckoutFromCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

export default CartContext;
